import { useToast } from '@chakra-ui/react'
import {
  faBolt,
  faCheck,
  faEye,
  faEyeSlash,
  faPen,
  faTrash,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useState } from 'react'
import InfoBox from '../../../../../../components/InfoBox'
import InvestmentPackageSelector from '../../../../../../components/InvestmentPackageSelector'
import Loader from '../../../../../../components/Loader'
import Toast from '../../../../../../components/Toast'
import { INVESTMENT_PACKAGE_LABELS } from '../../../../../../constants/investmentPackages'
import {
  useCollectOkxBalanceDataMutation,
  useCreateOkxAccountMutation,
  useDeleteOkxAccountMutation,
  useGetOkxAccountsQuery,
  useProvisionOkxSubAccountMutation,
  useUpdateOkxAccountMutation,
} from '../../../../../../redux/services/coreApi'
import type {
  CreateOkxAccountDto,
  InvestmentPackage,
  UpdateOkxAccountDto,
  UserDto,
} from '../../../../../../types/coreApi-types'

interface OkxIntegrationProps {
  client?: UserDto
}

const OkxIntegration: React.FC<OkxIntegrationProps> = ({ client }) => {
  const toast = useToast()

  // API hooks
  const { data: okxAccounts, isLoading: isLoadingAccounts } =
    useGetOkxAccountsQuery(
      { investorId: client?.id ?? '' },
      { skip: !client?.id }
    )
  const [createOkxAccount, { isLoading: isCreatingAccount }] =
    useCreateOkxAccountMutation()
  const [updateOkxAccount, { isLoading: isUpdatingAccount }] =
    useUpdateOkxAccountMutation()
  const [deleteOkxAccount, { isLoading: isDeletingAccount }] =
    useDeleteOkxAccountMutation()
  const [collectOkxBalanceData, { isLoading: isCollectingBalance }] =
    useCollectOkxBalanceDataMutation()
  const [provisionOkxSubAccount, { isLoading: isProvisioning }] =
    useProvisionOkxSubAccountMutation()

  // Form state for creating new accounts
  const [formData, setFormData] = useState<CreateOkxAccountDto>({
    name: '',
    description: '',
    apiKey: '',
    apiSecret: '',
    apiPassphrase: '',
    isActive: true,
    investmentPackage: null,
  })

  const [showSecrets, setShowSecrets] = useState({
    apiSecret: false,
    apiPassphrase: false,
  })

  // Inline edit state
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null)
  const [editFormData, setEditFormData] = useState<UpdateOkxAccountDto>({
    name: '',
    description: null,
    investmentPackage: null,
  })

  // Auto-provision state
  const [selectedPackage, setSelectedPackage] =
    useState<InvestmentPackage | null>(null)

  if (!client) {
    return (
      <div className="flex w-full h-full justify-center items-center">
        <Loader />
      </div>
    )
  }

  const handleCreateAccount = async () => {
    if (
      !formData.name ||
      !formData.apiKey ||
      !formData.apiSecret ||
      !formData.apiPassphrase
    ) {
      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="error">Please fill in all required fields</Toast>
        ),
      })
      return
    }

    try {
      await createOkxAccount({
        investorId: client.id,
        account: formData,
      }).unwrap()

      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="success">
            OKX account has been created successfully
          </Toast>
        ),
      })

      setFormData({
        name: '',
        description: '',
        apiKey: '',
        apiSecret: '',
        apiPassphrase: '',
        isActive: true,
        investmentPackage: null,
      })
    } catch (error) {
      toast({
        position: 'bottom-right',
        render: () => <Toast type="error">Failed to create OKX account</Toast>,
      })
    }
  }

  const handleDeleteAccount = async (accountId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this OKX account? This action cannot be undone.'
      )
    ) {
      return
    }

    try {
      await deleteOkxAccount({
        investorId: client.id,
        okxAccountId: accountId,
      }).unwrap()

      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="success">
            OKX account has been deleted successfully
          </Toast>
        ),
      })
    } catch (error) {
      toast({
        position: 'bottom-right',
        render: () => <Toast type="error">Failed to delete OKX account</Toast>,
      })
    }
  }

  const handleStartEdit = (account: {
    id: string
    name: string
    description?: string | null
    investmentPackage?: InvestmentPackage | null
  }) => {
    setEditingAccountId(account.id)
    setEditFormData({
      name: account.name,
      description: account.description ?? null,
      investmentPackage: account.investmentPackage ?? null,
    })
  }

  const handleCancelEdit = () => {
    setEditingAccountId(null)
  }

  const handleSaveEdit = async () => {
    if (!editingAccountId || !editFormData.name) {
      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="error">Account name is required</Toast>
        ),
      })
      return
    }

    try {
      await updateOkxAccount({
        investorId: client.id,
        okxAccountId: editingAccountId,
        account: editFormData,
      }).unwrap()

      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="success">OKX account updated successfully</Toast>
        ),
      })
      setEditingAccountId(null)
    } catch (error) {
      toast({
        position: 'bottom-right',
        render: () => <Toast type="error">Failed to update OKX account</Toast>,
      })
    }
  }

  const toggleSecretVisibility = (field: 'apiSecret' | 'apiPassphrase') => {
    setShowSecrets(prev => ({
      ...prev,
      [field]: !prev[field],
    }))
  }

  const handleCollectBalanceData = async () => {
    try {
      const result = await collectOkxBalanceData({
        investorId: client.id,
      }).unwrap()

      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="success">
            Balance data collection completed. Processed:{' '}
            {result.totalProcessed}, Success: {result.successCount}, Errors:{' '}
            {result.errorCount}
          </Toast>
        ),
      })
    } catch (error) {
      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="error">Failed to collect OKX balance data</Toast>
        ),
      })
    }
  }

  const handleProvisionSubAccount = async () => {
    if (!selectedPackage) {
      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="error">Please select an investment package</Toast>
        ),
      })
      return
    }

    const packageLabel = INVESTMENT_PACKAGE_LABELS[selectedPackage]

    if (
      !window.confirm(
        `This will create an OKX sub-account for this investor for "${packageLabel}". ` +
          `A read-only API key will be generated and stored securely. Continue?`
      )
    ) {
      return
    }

    try {
      const result = await provisionOkxSubAccount({
        investorId: client.id,
        investmentPackage: selectedPackage,
      }).unwrap()

      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="success">
            OKX sub-account provisioned for {packageLabel} (
            {result.subAccountName})
          </Toast>
        ),
      })

      setSelectedPackage(null)
    } catch (error: any) {
      const errorMessage =
        error?.data?.errors ||
        error?.data ||
        'Failed to provision OKX sub-account'
      toast({
        position: 'bottom-right',
        render: () => <Toast type="error">{String(errorMessage)}</Toast>,
      })
    }
  }

  const hasAccounts = okxAccounts && okxAccounts.length > 0

  // Determine which packages already have an auto-provisioned account
  const provisionedPackages = new Set(
    okxAccounts
      ?.filter(a => a.okxSubAccountName && a.isActive && a.investmentPackage)
      .map(a => a.investmentPackage as InvestmentPackage) ?? []
  )

  return (
    <div className="space-y-6 w-full">
      <div>
        <h1 className="h1">OKX Integration Management</h1>
      </div>

      {/* Current Status */}
      <div className="card">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="h2">Integration Status</h2>
            <InfoBox
              colorScheme={hasAccounts ? 'green' : 'gray'}
              className="text-xs"
            >
              {hasAccounts
                ? `${okxAccounts.length} Account${okxAccounts.length > 1 ? 's' : ''} Connected`
                : 'No Accounts Connected'}
            </InfoBox>
          </div>
          <p className="text-description">
            {hasAccounts
              ? `This investor has ${okxAccounts.length} OKX account${okxAccounts.length > 1 ? 's' : ''} configured.`
              : 'This investor does not have any OKX accounts configured.'}
          </p>
        </div>
      </div>

      {/* Auto-Provision Sub-Account */}
      <div className="card border-2 border-brand-accent/20 dark:border-brand-accent/30">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon
              icon={faBolt}
              className="w-5 h-5 text-brand-accent"
            />
            <h2 className="h2">Provision Sub-Account</h2>
          </div>
          <p className="text-description">
            Automatically create an OKX sub-account for this investor for a
            given investment package. A read-only API key will be generated and
            stored securely — the admin then fills the sub-account with the
            deposited funds, and the investor can follow their portfolio in the
            dashboard.
          </p>

          {provisionedPackages.size > 0 && (
            <div className="flex flex-wrap gap-2">
              {Array.from(provisionedPackages).map(pkg => (
                <InfoBox key={pkg} colorScheme="green" className="text-xs">
                  {INVESTMENT_PACKAGE_LABELS[pkg]} provisioned
                </InfoBox>
              ))}
            </div>
          )}

          <InvestmentPackageSelector
            label="Investment Package"
            value={selectedPackage}
            onChange={setSelectedPackage}
          />

          {selectedPackage && provisionedPackages.has(selectedPackage) && (
            <InfoBox colorScheme="orange" className="text-sm">
              This investor already has an active sub-account for{' '}
              {INVESTMENT_PACKAGE_LABELS[selectedPackage]}.
            </InfoBox>
          )}

          <button
            onClick={handleProvisionSubAccount}
            disabled={
              isProvisioning ||
              !selectedPackage ||
              (selectedPackage != null &&
                provisionedPackages.has(selectedPackage))
            }
            className="button w-full sm:w-auto flex items-center justify-center gap-2"
          >
            {isProvisioning ? (
              <Loader />
            ) : (
              <>
                <FontAwesomeIcon icon={faBolt} className="w-4 h-4" />
                Provision Sub-Account
              </>
            )}
          </button>
        </div>
      </div>

      {/* Existing Accounts */}
      {hasAccounts && (
        <div className="card">
          <div className="space-y-4">
            <h2 className="h2">Connected OKX Accounts</h2>
            <div className="space-y-3">
              {okxAccounts.map(account => (
                <div
                  key={account.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3"
                >
                  {editingAccountId === account.id ? (
                    /* Edit mode */
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-description text-xs">
                          Account Name{' '}
                          <span className="text-brand-light-red">*</span>
                        </label>
                        <input
                          type="text"
                          value={editFormData.name}
                          onChange={e =>
                            setEditFormData(prev => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          className="input"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-description text-xs">
                          Description
                        </label>
                        <input
                          type="text"
                          value={editFormData.description ?? ''}
                          onChange={e =>
                            setEditFormData(prev => ({
                              ...prev,
                              description: e.target.value || null,
                            }))
                          }
                          placeholder="Optional description"
                          className="input"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-description text-xs">
                          Investment Package
                        </label>
                        <select
                          value={editFormData.investmentPackage ?? ''}
                          onChange={e =>
                            setEditFormData(prev => ({
                              ...prev,
                              investmentPackage:
                                (e.target.value as InvestmentPackage) || null,
                            }))
                          }
                          className="input"
                        >
                          <option value="">None</option>
                          {(
                            Object.entries(INVESTMENT_PACKAGE_LABELS) as [
                              InvestmentPackage,
                              string,
                            ][]
                          ).map(([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={handleSaveEdit}
                          disabled={isUpdatingAccount}
                          className="button flex items-center gap-2"
                        >
                          {isUpdatingAccount ? (
                            <Loader />
                          ) : (
                            <>
                              <FontAwesomeIcon
                                icon={faCheck}
                                className="w-3 h-3"
                              />
                              Save
                            </>
                          )}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={isUpdatingAccount}
                          className="button-secondary flex items-center gap-2"
                        >
                          <FontAwesomeIcon
                            icon={faXmark}
                            className="w-3 h-3"
                          />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* View mode */
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {account.name}
                          </h3>
                          <InfoBox
                            colorScheme={account.isActive ? 'green' : 'gray'}
                            className="text-xs"
                          >
                            {account.isActive ? 'Active' : 'Inactive'}
                          </InfoBox>
                          {account.okxSubAccountName && (
                            <InfoBox colorScheme="green" className="text-xs">
                              Auto-provisioned
                            </InfoBox>
                          )}
                          {account.investmentPackage ? (
                            <InfoBox colorScheme="orange" className="text-xs">
                              {INVESTMENT_PACKAGE_LABELS[
                                account.investmentPackage
                              ] ?? account.investmentPackage}
                            </InfoBox>
                          ) : (
                            <InfoBox colorScheme="gray" className="text-xs">
                              No Package
                            </InfoBox>
                          )}
                        </div>
                        {account.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {account.description}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          API Key: {account.apiKey.substring(0, 8)}...
                        </p>
                        {account.okxSubAccountName && (
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            Sub-Account: {account.okxSubAccountName}
                            {account.okxSubAccountUid &&
                              ` (UID: ${account.okxSubAccountUid})`}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          Created:{' '}
                          {new Date(account.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleStartEdit(account)}
                          disabled={isUpdatingAccount}
                          className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                          title="Edit Account"
                        >
                          <FontAwesomeIcon
                            icon={faPen}
                            className="w-4 h-4"
                          />
                        </button>
                        <button
                          onClick={() => handleDeleteAccount(account.id)}
                          disabled={isDeletingAccount}
                          className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                          title="Delete Account"
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="w-4 h-4"
                          />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create New Account Form (Manual) */}
      <div className="card">
        <div className="space-y-4">
          <h2 className="h2 mb-4">Add OKX Account Manually</h2>
          <p className="text-description text-sm mb-2">
            Use this form to manually add an OKX account with existing API
            credentials. For new investors, prefer the auto-provision option
            above.
          </p>

          <div className="space-y-1">
            <label className="text-description">
              Account Name <span className="text-brand-light-red">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={e =>
                setFormData(prev => ({ ...prev, name: e.target.value }))
              }
              placeholder="Enter account name (e.g., Main Trading Account)"
              className="input"
            />
          </div>

          <div className="space-y-1">
            <label className="text-description">Description</label>
            <input
              type="text"
              value={formData.description || ''}
              onChange={e =>
                setFormData(prev => ({ ...prev, description: e.target.value }))
              }
              placeholder="Optional description for this account"
              className="input"
            />
          </div>

          <div className="space-y-1">
            <label className="text-description">
              API Key <span className="text-brand-light-red">*</span>
            </label>
            <input
              type="text"
              value={formData.apiKey}
              onChange={e =>
                setFormData(prev => ({ ...prev, apiKey: e.target.value }))
              }
              placeholder="Enter OKX API Key"
              className="input"
            />
          </div>

          <div className="space-y-1">
            <label className="text-description">
              API Secret <span className="text-brand-light-red">*</span>
            </label>
            <div className="relative">
              <input
                type={showSecrets.apiSecret ? 'text' : 'password'}
                value={formData.apiSecret}
                onChange={e =>
                  setFormData(prev => ({ ...prev, apiSecret: e.target.value }))
                }
                placeholder="Enter OKX API Secret"
                className="input pr-12"
              />
              <button
                type="button"
                onClick={() => toggleSecretVisibility('apiSecret')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-description-subtle hover:text-brand-light-text-description dark:hover:text-brand-accent transition-colors"
              >
                <FontAwesomeIcon
                  icon={showSecrets.apiSecret ? faEyeSlash : faEye}
                  className="w-4 h-4"
                />
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-description">
              API Passphrase <span className="text-brand-light-red">*</span>
            </label>
            <div className="relative">
              <input
                type={showSecrets.apiPassphrase ? 'text' : 'password'}
                value={formData.apiPassphrase}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    apiPassphrase: e.target.value,
                  }))
                }
                placeholder="Enter OKX API Passphrase"
                className="input pr-12"
              />
              <button
                type="button"
                onClick={() => toggleSecretVisibility('apiPassphrase')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-description-subtle hover:text-brand-light-text-description dark:hover:text-brand-accent transition-colors"
              >
                <FontAwesomeIcon
                  icon={showSecrets.apiPassphrase ? faEyeSlash : faEye}
                  className="w-4 h-4"
                />
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-description">Investment Package</label>
            <select
              value={formData.investmentPackage ?? ''}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  investmentPackage:
                    (e.target.value as InvestmentPackage) || null,
                }))
              }
              className="input"
            >
              <option value="">None</option>
              {(
                Object.entries(INVESTMENT_PACKAGE_LABELS) as [
                  InvestmentPackage,
                  string,
                ][]
              ).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={e =>
                setFormData(prev => ({ ...prev, isActive: e.target.checked }))
              }
              className="rounded"
            />
            <label className="text-description">Set account as active</label>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              onClick={handleCreateAccount}
              disabled={isCreatingAccount}
              className="button flex-1"
            >
              {isCreatingAccount ? <Loader /> : 'Create OKX Account'}
            </button>
          </div>
        </div>
      </div>

      {/* Manual Balance Collection */}
      {hasAccounts && (
        <div className="card">
          <div className="space-y-4">
            <h2 className="h2">Manual Balance Collection</h2>
            <p className="text-description">
              Manually trigger the collection of OKX balance data for all active
              accounts of this investor. This will fetch the latest balance
              information from their OKX accounts.
            </p>
            <button
              onClick={handleCollectBalanceData}
              disabled={isCollectingBalance}
              className="button w-full sm:w-auto"
            >
              {isCollectingBalance ? <Loader /> : 'Collect Balance Data'}
            </button>
          </div>
        </div>
      )}

      {/* Information Box */}
      <div className="card bg-brand-blue-tint dark:bg-brand-dark-card">
        <div className="space-y-3">
          <h2 className="h2 text-brand-blue dark:text-white">
            Important Information
          </h2>
          <div className="space-y-2 text-description text-brand-blue dark:text-brand-dark-text-description">
            <p>
              • <strong>Provision Sub-Account</strong> creates an OKX
              sub-account and read-only API key automatically for a given
              investment package — no manual credential exchange needed
            </p>
            <p>
              • After provisioning, deposit the investor's funds into their OKX
              sub-account — they can then track their portfolio in the dashboard
            </p>
            <p>
              • API credentials are securely stored on our servers and are never
              exposed to the admin or investor
            </p>
            <p>
              • Auto-provisioned API keys are read-only (least privilege) since
              they are only used to read balance data
            </p>
            <p>• Each account can be activated or deactivated independently</p>
            <p>
              • The investor can view combined performance data from all active
              accounts
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OkxIntegration
